import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  closeReceiptRequest,
  countReceiptRequest,
  getReceiptRequest,
  getReceiptsRequest,
} from './operatorReceivingApi';
import {
  OPEN_RECEIPT_STATUSES,
  RECEIVING_STEP,
  buildCountBody,
  countExcess,
  countIntent,
  draftFromLine,
  findReceivingSlot,
  lineOption,
  matchReceiptLines,
} from './operatorReceivingUtils';
import { scanCodeRequest } from '../operator/operatorApi';
import { matchCodes } from '../operator/operatorCodes';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, lostWriteOf, sameAction, sendWrite } from '../operator/operatorIdempotency';
import { SCAN_GATE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { offScreenMeta } from '../operator/operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';
import { receiptStatusLabel } from '../../utils/inventoryLabels';
import { receivingExcessFound, receivingLineChosen } from './operatorReceivingActions';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());
const selectReceiving = (state) => state.operatorReceiving;

/** The receipt as its screen titled it: «REC-000090», or its number if it never loaded here. */
const receiptName = (receiving, receiptId) => receiving.names[receiptId] || `Recepción #${receiptId}`;

// Keyed by receipt, so a write left out on one does not keep the next one's buttons busy.
const pendingByReceipt = ({ arg }) => ({ pendingKey: arg?.receiptId });

/** Open receipts, a page at a time; page 1 replaces, the rest append. */
export const fetchReceivingQueue = createAsyncThunk(
  'operatorReceiving/fetchQueue',
  async ({ page = 1 } = {}, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await getReceiptsRequest(warehouseOf(getState), { page });
      return fulfillWithValue(data, { receivedAt: Date.now() });
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar las recepciones.'));
    }
  }
);

export const fetchReceipt = createAsyncThunk(
  'operatorReceiving/fetchReceipt',
  async (receiptId, { getState, rejectWithValue }) => {
    try {
      const { data } = await getReceiptRequest(warehouseOf(getState), receiptId);
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos abrir la recepción.'));
    }
  }
);

/**
 * Where a write's answer lands, read when it arrives. A receipt off screen
 * gets the band named after it (`meta.offScreenTask`) and is not reloaded.
 * The receipt on screen takes the answer only if it is the write its visit
 * waits for; any other — sent on an earlier visit — reloads it instead, so
 * the screen shows how the receipt stands.
 *
 * @param {{getState: Function, dispatch: Function, requestId: string}} thunkApi
 * @param {number} receiptId
 */
const answerMeta = ({ getState, dispatch, requestId }, receiptId) => {
  const state = getState();
  const receiving = state.operatorReceiving;
  const meta = offScreenMeta(state, OPERATOR_FLOW.RECEIVING, receiptId, receiptName(receiving, receiptId));
  if (meta) return meta;
  if (receiving.lastWrite?.key === requestId) return undefined;
  dispatch(fetchReceipt(receiptId));
  return { earlierVisit: true };
};

/**
 * The receipt a scanned guide code opens, from the queue. Answers its id; a
 * closed receipt or a code that is not one is a refusal. Once the queue is
 * off screen — the operator opened something else meanwhile — the answer
 * says so in `meta.offScreenTask`, and opens nothing.
 *
 * @param {{code: string, source: string}} read
 */
export const findReceipt = createAsyncThunk(
  'operatorReceiving/findReceipt',
  async ({ code }, { getState, fulfillWithValue, rejectWithValue }) => {
    const value = cleanCode(code);
    const queueMeta = () => offScreenMeta(getState(), OPERATOR_FLOW.RECEIVING, null, value);
    try {
      const { data } = await scanCodeRequest(warehouseOf(getState), value, ['receipt']);
      const receipt = (data.results || []).find((result) => result.kind === 'receipt');
      if (!receipt) return rejectWithValue(localError(`${value} no es una recepción de esta bodega.`), queueMeta());
      if (!OPEN_RECEIPT_STATUSES.includes(receipt.status)) {
        return rejectWithValue(
          localError(`${receipt.code} ya está ${receiptStatusLabel(receipt.status).toLowerCase()}.`),
          queueMeta()
        );
      }
      return fulfillWithValue(receipt, queueMeta());
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos buscar esa recepción.'), queueMeta());
    }
  },
  {
    condition: (read, { getState }) => {
      const { queue } = getState().operatorReceiving;
      return scanGate({ busy: queue.finding, lastCode: queue.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * A code read while counting, read against the receipt on screen: a
 * receiving slot while one has to be chosen, otherwise a product of its lines.
 * A line tapped on screen (`lineId`) is that one, even when its product is on
 * another line too. Nothing is sent: the count goes once the quantity is in.
 *
 * @param {{code: string, source: string, alternatives?: string[], lineId?: number}} read - `alternatives`: the other codes in view.
 */
export const scanReceivingCode = createAsyncThunk(
  'operatorReceiving/scanCode',
  async ({ code, alternatives = [], lineId }, { getState, fulfillWithValue, rejectWithValue }) => {
    const { receipt, step } = getState().operatorReceiving;
    const value = cleanCode(code);

    if (step === RECEIVING_STEP.SLOT) {
      const matched = [value, ...alternatives].find((candidate) => findReceivingSlot(receipt, candidate));
      return matched
        ? fulfillWithValue({ kind: 'slot', slot: findReceivingSlot(receipt, matched) }, { matched })
        : rejectWithValue(localError(`${value} no es una ubicación de recepción de esta bodega.`));
    }

    if (lineId != null) {
      const line = receipt.lines.find((item) => item.id === lineId);
      return line
        ? { kind: 'line', draft: draftFromLine(line, value, matchCodes(line.codes, value)?.factor ?? 1) }
        : rejectWithValue(localError('Esa línea ya no está en la recepción.'));
    }

    const matches = matchReceiptLines(receipt, value);
    if (matches.length === 1) {
      return { kind: 'line', draft: draftFromLine(matches[0].line, value, matches[0].factor) };
    }
    if (matches.length > 1) return { kind: 'choose', code: value, options: matches.map(lineOption) };

    // Until the first count locks it, a receiving slot's code changes where the goods land.
    const slot = !receipt.slot_locked && findReceivingSlot(receipt, value);
    if (slot) return { kind: 'slot', slot };

    return rejectWithValue(localError(`${value} no está en esta recepción.`));
  },
  {
    condition: (read, { getState }) => {
      const state = getState().operatorReceiving;
      const busy =
        !state.receipt || Boolean(state.pending || state.resolution) || state.step === RECEIVING_STEP.QUANTITY;
      return scanGate({ busy, lastCode: state.scan.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * Registers what arrived of one line. Over what the line lacks, the server
 * registers it only with `confirmExcess`; without it, it answers 409
 * `over_receipt` with the lines in `excess`.
 *
 * @param {{receiptId: number, code: string, lineId: number, factor: number, quantity: number,
 *   lot?: string, expiryDate?: string, slotCode?: string, confirmExcess?: boolean, idempotencyKey?: string}} arg
 */
export const countReceipt = createAsyncThunk(
  'operatorReceiving/count',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await countReceiptRequest(
        warehouseOf(getState),
        arg.receiptId,
        buildCountBody(arg, requestId)
      );
      return fulfillWithValue(data, answerMeta(thunkApi, arg.receiptId));
    } catch (error) {
      return rejectWithValue(
        readOperatorError(error, 'No pudimos registrar el conteo.'),
        answerMeta(thunkApi, arg.receiptId)
      );
    }
  },
  { ...idempotentThunkOptions, getPendingMeta: pendingByReceipt }
);

/**
 * The count kept because its answer was lost, when `arg` is that count again:
 * whether it went confirmed or not, it meant the same.
 *
 * @param {object} receiving - The slice.
 * @param {object} arg
 */
export const lostCountOf = (receiving, arg) => lostWriteOf(countReceipt, arg, receiving, countIntent);

/**
 * A count as its screen sends it. The count whose answer was lost goes again
 * as it went, under its key, even if the line now looks over: the server says
 * whether it already counted. Any other count that goes over what its line
 * lacks waits for the operator to confirm it, and sends nothing.
 *
 * @param {object} arg - As `countReceipt` takes it, without `confirmExcess`.
 */
export const submitCount = (arg) => (dispatch, getState) => {
  const receiving = selectReceiving(getState());
  const lost = lostCountOf(receiving, arg);
  if (lost) return dispatch(countReceipt(sameAction(lost)));
  const excess = countExcess(receiving.receipt, arg);
  if (excess.length > 0) return dispatch(receivingExcessFound({ arg: countIntent(arg), excess }));
  return dispatch(countReceipt(arg));
};

/**
 * «Registrar igual»: the count the sheet holds, with `confirmExcess`. It is
 * another action than the count that asked — another body, a key of its own —
 * and only the same confirmed count after a lost answer keeps its key.
 */
export const confirmExcessCount = () => (dispatch, getState) => {
  const { resolution } = selectReceiving(getState());
  if (resolution?.kind !== 'excess') return null;
  return dispatch(sendWrite(countReceipt, { ...resolution.arg, confirmExcess: true }, selectReceiving));
};

/**
 * Closes the receipt. Short of what was expected, the server asks for a
 * confirmation first. It has no key: closing twice answers `already_closed`.
 *
 * @param {{receiptId: number, confirmShortage?: boolean}} arg
 */
export const closeReceipt = createAsyncThunk(
  'operatorReceiving/close',
  async ({ receiptId, confirmShortage = false }, thunkApi) => {
    const { getState, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await closeReceiptRequest(
        warehouseOf(getState),
        receiptId,
        confirmShortage ? { confirm_shortage: true } : {}
      );
      return fulfillWithValue(data, answerMeta(thunkApi, receiptId));
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cerrar la recepción.'), answerMeta(thunkApi, receiptId));
    }
  },
  { getPendingMeta: pendingByReceipt }
);

/** "Contar otra igual": the last count once more, as a new action; over what its line lacks, it asks first. */
export const repeatLastCount = () => (dispatch, getState) => {
  const { lastCount, receipt } = selectReceiving(getState());
  if (!lastCount) return null;
  const excess = countExcess(receipt, lastCount);
  return dispatch(excess.length > 0 ? receivingExcessFound({ arg: lastCount, excess }) : countReceipt(lastCount));
};

/**
 * The line the operator chose for an ambiguous code. Chosen on the phone, it
 * just opens the quantity; chosen for the server, the same count goes again
 * with the line and the same key.
 *
 * @param {number} lineId
 */
export const chooseReceivingLine = (lineId) => (dispatch, getState) => {
  const { resolution } = getState().operatorReceiving;
  if (!resolution || resolution.kind !== 'ambiguous') return null;
  if (resolution.origin === 'local') {
    const option = resolution.options.find((item) => item.id === lineId);
    const line = getState().operatorReceiving.receipt.lines.find((item) => item.id === lineId);
    return dispatch(receivingLineChosen(draftFromLine(line, resolution.code, option?.factor || 1)));
  }
  return dispatch(countReceipt(sameAction({ arg: { ...resolution.arg, lineId }, key: resolution.key })));
};
